import { type Mesh } from './mesh';
import { type Vec3 } from './Vec3';

/**
 * The convex polyhedron on a set of points in space.
 *
 * ## Why a hull rather than a list of faces
 *
 * Because the faces of a convex body are *implied* by its corners, and asking for them is asking
 * a teacher to do bookkeeping a computer is better at. "Tứ diện ABCD" names four points and every
 * reader knows which four triangles follow; a syntax that made them spell out `0 1 2`, `0 1 3`,
 * `1 2 3`, `0 2 3` would be a worse way of saying the same thing, wrong in a way nothing catches
 * — a mistyped face draws a solid with a hole in it — and unusable for the case that matters
 * most, which is a solid cut out of another one.
 *
 * Non-convex bodies still need their faces stated, and will get a way to state them. This covers
 * everything a convex hull covers, which is a tetrahedron on any four points, a wedge, a
 * truncated prism, and the piece left when a plane cuts a convex solid.
 *
 * ## The algorithm, and why this one
 *
 * Incremental: start with a tetrahedron of four points that are not coplanar, then add each
 * remaining point by deleting every face it can see and stitching the resulting hole to it. It is
 * the textbook construction, it is exact in the sense that matters here — a face is kept or
 * dropped by the sign of a determinant, never by a distance threshold — and at the scale this
 * deals with, a handful of corners, its quadratic worst case is beneath notice.
 *
 * The alternative worth naming is quickhull, which is faster on thousands of points and has more
 * ways to go wrong on four. A solid in a school figure has between four and a dozen corners.
 *
 * ## Degeneracy is refused, not repaired
 *
 * Four coplanar points describe a quadrilateral, not a solid, and there is no honest hull to
 * return — the volume is zero. Nudging them apart would draw a body the problem did not state.
 * `null` is the answer, and the caller reports it.
 */

/** How far off a plane a point must be to count as off it, relative to the body's own size. */
const FLAT = 1e-9;

/** A face while the hull is being built: three corner indices, wound to face outward. */
interface Facet {
  readonly a: number;
  readonly b: number;
  readonly c: number;
}

function subtract(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

/** Six times the signed volume of the tetrahedron — positive when `d` is outside the facet. */
function outside(points: readonly Vec3[], facet: Facet, d: Vec3): number {
  const a = points[facet.a];
  const b = points[facet.b];
  const c = points[facet.c];
  if (a === undefined || b === undefined || c === undefined) return 0;
  return dot(cross(subtract(b, a), subtract(c, a)), subtract(d, a));
}

/** The largest distance between any two points, for scaling the flatness tolerance. */
function spreadOf(points: readonly Vec3[]): number {
  let least = { x: Infinity, y: Infinity, z: Infinity };
  let most = { x: -Infinity, y: -Infinity, z: -Infinity };
  for (const point of points) {
    least = {
      x: Math.min(least.x, point.x),
      y: Math.min(least.y, point.y),
      z: Math.min(least.z, point.z),
    };
    most = {
      x: Math.max(most.x, point.x),
      y: Math.max(most.y, point.y),
      z: Math.max(most.z, point.z),
    };
  }
  const spread = Math.max(most.x - least.x, most.y - least.y, most.z - least.z);
  return Number.isFinite(spread) ? Math.max(spread, 0) : 0;
}

/**
 * Four points that genuinely span space, or `null`.
 *
 * Searched for rather than assumed to be the first four: a teacher listing the corners of a cube
 * face first and its opposite face after would otherwise be told their cube is flat.
 */
function seed(
  points: readonly Vec3[],
  slack: number
): readonly [number, number, number, number] | null {
  const count = points.length;
  for (let a = 0; a < count; a += 1) {
    for (let b = a + 1; b < count; b += 1) {
      for (let c = b + 1; c < count; c += 1) {
        const facet = { a, b, c };
        for (let d = c + 1; d < count; d += 1) {
          const point = points[d];
          if (point !== undefined && Math.abs(outside(points, facet, point)) > slack) {
            return [a, b, c, d];
          }
        }
      }
    }
  }
  return null;
}

/** Both ends of an edge, in a fixed order, so a shared edge is recognised however it was wound. */
const edgeKey = (a: number, b: number): string =>
  a < b ? `${String(a)}|${String(b)}` : `${String(b)}|${String(a)}`;

/**
 * The convex hull of `points`, as a mesh whose faces have been merged back into flat polygons.
 *
 * `null` when the points are coplanar, collinear, coincident or fewer than four — every one of
 * which describes something that is not a solid.
 */
export function hullOf(points: readonly Vec3[]): Mesh | null {
  if (points.length < 4) return null;

  const spread = spreadOf(points);
  if (spread === 0) return null;
  // Cubed, because the determinant is a volume: a tolerance in lengths would call a body a
  // thousand times too flat or too fat depending only on how large the figure is.
  const slack = spread ** 3 * FLAT;

  const start = seed(points, slack);
  if (start === null) return null;

  const [a, b, c, d] = start;
  const facets: Facet[] = [];

  // The seed tetrahedron, with every face wound to look outward from the fourth point.
  const point = points[d];
  if (point === undefined) return null;
  const flip = outside(points, { a, b, c }, point) > 0;
  const face = (x: number, y: number, z: number): Facet =>
    flip ? { a: x, b: z, c: y } : { a: x, b: y, c: z };
  facets.push(face(a, b, c), face(a, c, d), face(a, d, b), face(b, d, c));

  for (let index = 0; index < points.length; index += 1) {
    if (index === a || index === b || index === c || index === d) continue;
    const current = points[index];
    if (current === undefined) continue;

    const seen = facets.filter((facet) => outside(points, facet, current) > slack);
    if (seen.length === 0) continue;

    /*
     * The horizon: every edge of the visible region that the invisible region also owns. Edges
     * shared by two visible faces are interior to the hole and disappear with them.
     */
    const counts = new Map<string, { a: number; b: number; times: number }>();
    for (const facet of seen) {
      for (const [from, to] of [
        [facet.a, facet.b],
        [facet.b, facet.c],
        [facet.c, facet.a],
      ] as const) {
        const key = edgeKey(from, to);
        const found = counts.get(key);
        if (found === undefined) counts.set(key, { a: from, b: to, times: 1 });
        else found.times += 1;
      }
    }

    const kept = facets.filter((facet) => !seen.includes(facet));
    facets.length = 0;
    facets.push(...kept);
    for (const edge of counts.values()) {
      if (edge.times === 1) facets.push({ a: edge.a, b: edge.b, c: index });
    }
  }

  return meshFrom(points, facets, slack);
}

/**
 * The triangles merged back into the flat faces a reader sees.
 *
 * A hull is built from triangles and a cube is not drawn with diagonals across its faces. Coplanar
 * neighbours are joined, and the boundary of each group is walked out as one ring — which is also
 * what the hidden-line cutter wants, since a diagonal that is not a crease is a line the figure
 * never had.
 */
function meshFrom(points: readonly Vec3[], facets: readonly Facet[], slack: number): Mesh | null {
  if (facets.length === 0) return null;

  const normalOf = (facet: Facet): Vec3 | null => {
    const p = points[facet.a];
    const q = points[facet.b];
    const r = points[facet.c];
    if (p === undefined || q === undefined || r === undefined) return null;
    const n = cross(subtract(q, p), subtract(r, p));
    const length = Math.hypot(n.x, n.y, n.z);
    return length === 0 ? null : { x: n.x / length, y: n.y / length, z: n.z / length };
  };

  const groups: { normal: Vec3; anchor: Vec3; members: Facet[] }[] = [];
  for (const facet of facets) {
    const normal = normalOf(facet);
    const anchor = points[facet.a];
    if (normal === null || anchor === undefined) continue;

    const found = groups.find(
      (group) =>
        dot(group.normal, normal) > 1 - 1e-9 &&
        Math.abs(dot(group.normal, subtract(anchor, group.anchor))) <= slack ** (1 / 3) * 1e-6
    );
    if (found === undefined) groups.push({ normal, anchor, members: [facet] });
    else found.members.push(facet);
  }

  const faces: number[][] = [];
  for (const group of groups) {
    const ring = boundaryOf(group.members);
    if (ring !== null) faces.push(ring);
  }
  if (faces.length < 4) return null;

  const edges = new Map<string, { a: number; b: number; faces: number[] }>();
  faces.forEach((ring, index) => {
    ring.forEach((from, k) => {
      const to = ring[(k + 1) % ring.length] ?? from;
      const key = edgeKey(from, to);
      const found = edges.get(key);
      if (found === undefined) edges.set(key, { a: from, b: to, faces: [index] });
      else found.faces.push(index);
    });
  });

  return {
    vertices: points,
    faces,
    edges: [...edges.values()].map((edge) => ({ ...edge, hard: true })),
  };
}

/** The outer boundary of a group of triangles, as one ring. `null` if they do not form one. */
function boundaryOf(members: readonly Facet[]): number[] | null {
  const once = new Map<number, number>();
  for (const facet of members) {
    for (const [from, to] of [
      [facet.a, facet.b],
      [facet.b, facet.c],
      [facet.c, facet.a],
    ] as const) {
      // A directed edge appearing twice inside the group is interior and cancels its opposite.
      if (once.get(to) === from) once.delete(to);
      else once.set(from, to);
    }
  }
  if (once.size < 3) return null;

  const first = [...once.keys()][0];
  if (first === undefined) return null;

  const ring: number[] = [first];
  let at = once.get(first);
  while (at !== undefined && at !== first && ring.length <= once.size) {
    ring.push(at);
    at = once.get(at);
  }
  return at === first && ring.length === once.size ? ring : null;
}
